/**
 * WhatsAppSettings Component
 *
 * Main WhatsApp settings panel for connection management and notification preferences.
 * Supports QR code pairing, pairing code flow, test messaging, and notification toggles.
 *
 * Related Tickets: AC-1050
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageSquare,
  Phone,
  Wifi,
  WifiOff,
  Loader,
  Send,
  Check,
  AlertCircle,
  X,
  Bell,
  BellOff,
} from 'lucide-react';
import apiService from '../../services/api';
import WhatsAppQRCode from './WhatsAppQRCode';
import {
  WhatsAppStatus,
  WhatsAppNotificationPreferences,
} from '../../types';

/** Extract a user-friendly error message from an Axios error or generic error. */
function getErrorMessage(err: any, fallback: string): string {
  return err?.response?.data?.detail || err?.message || fallback;
}

type ConnectionPhase = 'idle' | 'connecting' | 'qr_scan' | 'pairing_code' | 'connected';

const WhatsAppSettings: React.FC = () => {
  // Connection state
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [phase, setPhase] = useState<ConnectionPhase>('idle');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // QR code state
  const [qrData, setQrData] = useState<string | null>(null);
  const [qrExpiresIn, setQrExpiresIn] = useState<number | null>(null);

  // Pairing code state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [isPairingLoading, setIsPairingLoading] = useState(false);

  // Test message state
  const [isSendingTest, setIsSendingTest] = useState(false);

  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState<WhatsAppNotificationPreferences | null>(null);
  const [isUpdatingPrefs, setIsUpdatingPrefs] = useState(false);

  // Polling ref
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Load status on mount
  useEffect(() => {
    loadStatus();
    return () => stopPolling();
  }, []);

  const loadStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const statusData = await apiService.whatsAppGetStatus();
      setStatus(statusData);
      if (statusData.connected) {
        setPhase('connected');
        loadNotificationPreferences();
      } else {
        setPhase('idle');
      }
    } catch (err: any) {
      console.error('[WhatsAppSettings] Failed to load status:', err);
      setStatus(null);
      setPhase('idle');
    } finally {
      setIsLoading(false);
    }
  };

  const loadNotificationPreferences = async () => {
    try {
      const prefs = await apiService.whatsAppGetNotificationPreferences();
      setNotifPrefs(prefs);
    } catch (err: any) {
      console.error('[WhatsAppSettings] Failed to load notification preferences:', err);
    }
  };

  const startPolling = useCallback(() => {
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const statusData = await apiService.whatsAppGetStatus();
        setStatus(statusData);
        if (statusData.connected) {
          setPhase('connected');
          stopPolling();
          loadNotificationPreferences();
          setSuccessMessage('WhatsApp connected successfully!');
        }
      } catch {
        // Ignore polling errors
      }
    }, 3000);
  }, []);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const handleConnect = async () => {
    try {
      setError(null);
      setPhase('connecting');
      const result = await apiService.whatsAppConnect();

      if (result.status === 'connected') {
        setPhase('connected');
        await loadStatus();
        return;
      }

      if (result.status === 'qr_pending' && result.qr_data) {
        setQrData(result.qr_data);
        setQrExpiresIn(60);
        setPhase('qr_scan');
        startPolling();
      } else {
        setPhase('qr_scan');
        await refreshQR();
        startPolling();
      }
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to initiate connection'));
      setPhase('idle');
    }
  };

  const refreshQR = async () => {
    try {
      const qrResult = await apiService.whatsAppGetQR();
      setQrData(qrResult.qr_data);
      setQrExpiresIn(qrResult.expires_in);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to refresh QR code'));
    }
  };

  const handleDisconnect = async () => {
    try {
      setError(null);
      await apiService.whatsAppDisconnect();
      setStatus(null);
      setPhase('idle');
      setNotifPrefs(null);
      setSuccessMessage('WhatsApp disconnected');
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to disconnect'));
    }
  };

  const handleRequestPairingCode = async () => {
    if (!phoneNumber.trim()) {
      setError('Please enter a phone number');
      return;
    }
    try {
      setError(null);
      setIsPairingLoading(true);
      // Strip +, spaces, and dashes — sidecar expects digits only
      const cleanNumber = phoneNumber.trim().replace(/[\s+\-()]/g, '');
      const result = await apiService.whatsAppRequestPairingCode(cleanNumber);
      setPairingCode(result.pairing_code);
      startPolling();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to request pairing code'));
    } finally {
      setIsPairingLoading(false);
    }
  };

  const handleSendTest = async () => {
    try {
      setError(null);
      setIsSendingTest(true);
      const result = await apiService.whatsAppSendTest();
      if (result.sent) {
        setSuccessMessage('Test message sent successfully!');
      } else {
        setError('Failed to send test message');
      }
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to send test message'));
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleToggleNotifPref = async (
    key: 'enabled' | 'notify_on_completion' | 'notify_on_failure',
    value: boolean
  ) => {
    try {
      setIsUpdatingPrefs(true);
      setError(null);
      const updated = await apiService.whatsAppUpdateNotificationPreferences({ [key]: value });
      setNotifPrefs(updated);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to update preferences'));
    } finally {
      setIsUpdatingPrefs(false);
    }
  };

  const switchToPairingCode = () => {
    stopPolling();
    setPhase('pairing_code');
    setQrData(null);
    setPairingCode(null);
  };

  const switchToQR = () => {
    setPairingCode(null);
    setPhoneNumber('');
    handleConnect();
  };

  const cancelConnection = () => {
    stopPolling();
    setPhase('idle');
    setQrData(null);
    setPairingCode(null);
    setPhoneNumber('');
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleString();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center mb-4">
          <MessageSquare className="w-5 h-5 text-green-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">WhatsApp</h3>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-40"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <MessageSquare className="w-5 h-5 text-green-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">WhatsApp</h3>
        </div>
        {/* Connection status badge */}
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            phase === 'connected'
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
          }`}
        >
          {phase === 'connected' ? (
            <>
              <Wifi className="w-3 h-3 mr-1" />
              Connected
            </>
          ) : (
            <>
              <WifiOff className="w-3 h-3 mr-1" />
              Disconnected
            </>
          )}
        </span>
      </div>

      <p className="text-gray-500 dark:text-gray-400 text-sm">
        Connect your WhatsApp account to receive workflow notifications.
      </p>

      {/* Error message */}
      {error && (
        <div className="flex items-start p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4 text-red-400 hover:text-red-600" />
          </button>
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <div className="flex items-start p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-green-700 dark:text-green-400">{successMessage}</p>
        </div>
      )}

      {/* Idle state - show connect button */}
      {phase === 'idle' && (
        <div className="space-y-3">
          <button
            onClick={handleConnect}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Connect WhatsApp
          </button>
        </div>
      )}

      {/* Connecting state */}
      {phase === 'connecting' && (
        <div className="flex items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Loader className="w-5 h-5 text-green-600 animate-spin mr-3" />
          <span className="text-sm text-gray-600 dark:text-gray-300">Connecting to WhatsApp...</span>
        </div>
      )}

      {/* QR Scan phase */}
      {phase === 'qr_scan' && (
        <div className="space-y-4">
          <WhatsAppQRCode
            qrData={qrData}
            expiresIn={qrExpiresIn}
            onExpired={() => {}}
            onRefresh={refreshQR}
          />
          <div className="flex items-center justify-between">
            <button
              onClick={switchToPairingCode}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              Use pairing code instead
            </button>
            <button
              onClick={cancelConnection}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Pairing Code phase */}
      {phase === 'pairing_code' && (
        <div className="space-y-4">
          {!pairingCode ? (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Phone Number
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Enter your phone number with country code (e.g., +1234567890)
              </p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+1234567890"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-warm-white text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={handleRequestPairingCode}
                  disabled={isPairingLoading || !phoneNumber.trim()}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isPairingLoading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    'Get Code'
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                Enter this code in WhatsApp on your phone:
              </p>
              <div className="text-3xl font-mono font-bold tracking-widest text-gray-900 dark:text-warm-white">
                {pairingCode}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                Go to WhatsApp Settings &gt; Linked Devices &gt; Link a Device &gt; Link with phone number instead
              </p>
            </div>
          )}
          <div className="flex items-center justify-between">
            <button
              onClick={switchToQR}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              Use QR code instead
            </button>
            <button
              onClick={cancelConnection}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Connected state */}
      {phase === 'connected' && status && (
        <div className="space-y-4">
          {/* Connection info */}
          <div className="p-4 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800">
            <div className="space-y-2">
              {status.phone_number && (
                <div className="flex items-center text-sm">
                  <Phone className="w-4 h-4 text-green-600 mr-2" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {status.phone_number}
                  </span>
                </div>
              )}
              {status.connected_at && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Connected since {formatDate(status.connected_at)}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleSendTest}
              disabled={isSendingTest}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              {isSendingTest ? (
                <Loader className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Test Message
            </button>
            <button
              onClick={handleDisconnect}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <WifiOff className="w-4 h-4 mr-2" />
              Disconnect
            </button>
          </div>

          {/* Notification Preferences */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
            <div className="flex items-center mb-3">
              <Bell className="w-4 h-4 text-gray-600 dark:text-gray-400 mr-2" />
              <h4 className="text-sm font-semibold text-gray-900 dark:text-warm-white">
                Notification Preferences
              </h4>
            </div>

            {notifPrefs ? (
              <div className="space-y-3">
                {/* Global enable */}
                <ToggleRow
                  label="Enable WhatsApp Notifications"
                  description="Receive workflow notifications via WhatsApp"
                  enabled={notifPrefs.enabled}
                  disabled={isUpdatingPrefs}
                  onChange={(val) => handleToggleNotifPref('enabled', val)}
                />
                {/* Notify on completion */}
                <ToggleRow
                  label="Notify on Completion"
                  description="Get notified when a workflow completes successfully"
                  enabled={notifPrefs.notify_on_completion}
                  disabled={isUpdatingPrefs || !notifPrefs.enabled}
                  onChange={(val) => handleToggleNotifPref('notify_on_completion', val)}
                />
                {/* Notify on failure */}
                <ToggleRow
                  label="Notify on Failure"
                  description="Get notified when a workflow fails"
                  enabled={notifPrefs.notify_on_failure}
                  disabled={isUpdatingPrefs || !notifPrefs.enabled}
                  onChange={(val) => handleToggleNotifPref('notify_on_failure', val)}
                />
              </div>
            ) : (
              <div className="animate-pulse space-y-3">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Reusable toggle row for notification settings.
 */
interface ToggleRowProps {
  label: string;
  description: string;
  enabled: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}

const ToggleRow: React.FC<ToggleRowProps> = ({ label, description, enabled, disabled, onChange }) => (
  <div className="flex items-center justify-between py-1">
    <div className="flex-1 min-w-0 mr-3">
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
    </div>
    <button
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${enabled ? 'bg-green-600' : 'bg-gray-200 dark:bg-gray-600'}`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  </div>
);

export default WhatsAppSettings;
